import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const EMPTY = { name: "", nipc: "", ami: "", slug: "", director_email: "", director_name: "", copy_from: "" };
const FIELDS = [["name", "Nome da agência", 2], ["nipc", "NIPC"], ["ami", "N.º da licença AMI"], ["slug", "Endereço (imodesk.pt/…)"],
  ["director_name", "Nome da Direção"], ["director_email", "Email da Direção (recebe o convite)", 2, "email"]];

export function NovaAgencia({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/platform/agencies").then(({ data }) => setList(data)).catch(() => {}); }, []);
  const suggest = async () => {
    if (!f.name.trim() || f.slug) { return; }
    try { const { data } = await api.get("/platform/slug-suggest", { params: { name: f.name } }); setF((x) => ({ ...x, slug: x.slug || data.slug })); } catch { /* sem sugestão */ }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/agencies/quick", { ...f, slug: f.slug || null, copy_from: f.copy_from || null });
      toast.success(`Agência «${data.name}» criada.${data.emailed ? " Convite enviado à Direção." : ""}`);
      setDone(data); setF(EMPTY); onCreated?.();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const clean = (k, v) => (k === "ami" ? v.replace(/\D/g, "").slice(0, 8) : k === "nipc" ? v.replace(/\D/g, "").slice(0, 9) : k === "slug" ? v.toLowerCase().replace(/[^a-z0-9-]/g, "") : v);
  return (
    <form onSubmit={submit} className={`${card} max-w-3xl`} data-testid="new-agency-form">
      <h3 className="font-heading text-lg text-ec-green">Nova agência</h3>
      <p className="text-sm text-slate-500 mb-5">A Direção recebe um convite por email, escolhe a palavra-passe e, no 1.º login, abre o assistente «Configurar a agência».</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, span, type]) => (
          <div key={k} className={span === 2 ? "sm:col-span-2" : ""}>
            <Label htmlFor={`na-${k}`}>{label}</Label>
            <Input id={`na-${k}`} type={type || "text"} required={!["slug", "director_name"].includes(k)} value={f[k]}
              onBlur={k === "name" ? suggest : undefined} onChange={(e) => setF({ ...f, [k]: clean(k, e.target.value) })} className="mt-1.5" data-testid={`new-agency-${k}`} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Label htmlFor="na-copy">Copiar definições de…</Label>
          <select id="na-copy" value={f.copy_from} onChange={(e) => setF({ ...f, copy_from: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="new-agency-copy">
            <option value="">Não copiar</option>
            {list.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <p className="text-xs text-slate-500 mt-1">Copia a minuta do CMI, a gestão processual, as mensagens e o «Responder para». Nunca copia o logótipo, clientes nem utilizadores.</p>
        </div>
      </div>
      <button type="submit" disabled={busy} className="mt-5 bg-ec-green text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-60" data-testid="new-agency-submit">
        {busy ? "A criar…" : "Criar agência e convidar a Direção"}
      </button>
      {done && (
        <div className="mt-4 text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="new-agency-done">
          <span>{done.name} · imodesk.pt/{done.slug}/entrar</span>
          <button type="button" onClick={() => { navigator.clipboard.writeText(done.invite_url); toast.success("Link do convite copiado"); }} className="inline-flex items-center gap-1 text-ec-green font-semibold" data-testid="new-agency-copy-invite"><Copy size={13} /> Copiar convite</button>
        </div>
      )}
    </form>
  );
}
